/* eslint-disable no-console */
import { createReference, getExtensionValue, isEmpty } from "@medplum/core";
import {
  Annotation,
  BundleEntry,
  CodeableConcept,
  Coding,
  Observation,
  Patient,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  Reference,
  Resource,
} from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

import { SentryInstance } from "../../utils/sentry";

export enum Systems {
  LOINC = "http://loinc.org",
  IdentifierType = "http://terminology.hl7.org/CodeSystem/v2-0203",
  SocialSecurity = "http://hl7.org/fhir/sid/us-ssn",
  GenderIdentity = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity",
  Pronouns = "http://hl7.org/fhir/StructureDefinition/individual-pronouns",
  SexualOrientation = "http://hl7.org/fhir/us/cdmh/StructureDefinition/cdmh-patient-sexualOrientation",
  SexAssignedAtBirth = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex",
  CanCallToPreferredContact = "http://quiltedhealth.com/sd/can-call-to-preferred-contact",
  CanLeaveVoicemailToPreferredContact = "http://quiltedhealth.com/sd/can-leave-voicemail-to-preferred-contact",
  MedicaidEligibility = "http://quiltedhealth.com/fhir/ValueSet/medicaid-eligibility",
  BirthPlace = "http://hl7.org/fhir/StructureDefinition/patient-birthPlace",
  GeneticParentIsCoParent = "http://quiltedhealth.com/sd/genetic-parent-is-co-parent",
  GeneticParentWillBeOnBirthCertificate = "http://quiltedhealth.com/sd/genetic-parent-will-be-on-birth-certificate",
  SupportNetworkPartnerStatusComments = "http://quiltedhealth.com/sd/support-network-partner-status-comments",
  SupportNetworkPartnerFormsetCode = "http://quiltedhealth.com/sd/support-network-partner-formset-code",
  QH = "http://quiltedhealth.com",
  UnitsOfMeasure = "http://unitsofmeasure.org",
  RelationshipRoles = "http://terminology.hl7.org/CodeSystem/v3-RoleClass",
  ConsentAction = "http://terminology.hl7.org/CodeSystem/consentaction",
  ConsentScope = "http://terminology.hl7.org/CodeSystem/consentscope",
  ConsentPolicyCodes = "http://terminology.hl7.org/CodeSystem/consentpolicycodes",
  SurgeryBloodTransfusion = "http://quiltedhealth.com/sd/surgery-blood-transfusion",
  Snomed = "http://snomed.info/sct",
  BreastSurgeryType = "http://quiltedhealth.com/fhir/CodeSystem/breast-surgery-type",
  LoincPronouns = "http://loinc.org/vs/LL5144-2",
  ResourceClientVisibility = "http://quiltedhealth.com/fhir/ValueSet/resource-client-visibility",
  ConditionCategory = "http://terminology.hl7.org/CodeSystem/condition-category",
  ProblemType = "http://quiltedhealth.com/fhir/ValueSet/problem-type",
  ConditionClinicalStatus = "http://terminology.hl7.org/CodeSystem/condition-clinical",
  V3ActCode = "http://terminology.hl7.org/CodeSystem/v3-ActCode",
  AllergyIntoleranceClinicalStatus = "http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical",
}

export type Answers = Record<string, QuestionnaireResponseItemAnswer[]>;

export type ObservationValues = {
  valueString?: string;
  valueDateTime?: string;
  valueInteger?: number;
  valueCodeableConcept?: CodeableConcept;
  hasMember?: Reference<Observation>[] | { reference: string }[];
  valueBoolean?: boolean;
  note?: Annotation[];
};

const ANSWER_TYPES = [
  "valueBoolean",
  "valueDecimal",
  "valueInteger",
  "valueDate",
  "valueDateTime",
  "valueTime",
  "valueString",
  "valueUri",
  "valueAttachment",
  "valueQuantity",
  "valueReference",
  "valueCoding",
] as const;

export function logBundleEntryError(
  entry: BundleEntry<Resource>,
  section: string,
  sentry: SentryInstance | null,
): void {
  if (sentry) {
    sentry.captureException(new Error(`[${section}] Error processing entry`), {
      extra: { response: entry.response },
    });
  } else {
    console.log(`[${section}] Error processing entry`, entry.response);
  }
}

export function get(items: QuestionnaireResponseItemAnswer[], position: number = 0) {
  const item = items?.[position];
  if (!item) return;

  const availableKey = ANSWER_TYPES.find((key) => !isEmpty(item[key]));
  if (!availableKey) return;

  return item[availableKey];
}

export function createObservation(
  code: CodeableConcept,
  value: ObservationValues,
  patient: Patient,
  note?: Annotation[],
  status: "registered" | "final" = "registered",
  derivedFrom?: Reference<QuestionnaireResponse>[],
): Observation {
  return {
    resourceType: "Observation",
    status,
    code,
    subject: createReference(patient),
    ...value,
    ...(note && (Array.isArray(note) ? (note.length ? { note } : {}) : { note })),
    ...(derivedFrom && { derivedFrom }),
  };
}

export function getFormsetAnswers(
  response: QuestionnaireResponse,
  linkId: string,
  groupByLinkId: string,
): Record<string, Record<string, QuestionnaireResponseItemAnswer>[]> {
  const findQuestionnaireResponseItems = (
    questionnaireResponseItems: QuestionnaireResponseItem[] | undefined,
    linkId: string,
  ): QuestionnaireResponseItem[] => {
    const result: QuestionnaireResponseItem[] = [];

    for (const item of questionnaireResponseItems ?? []) {
      if (item.linkId === linkId) {
        result.push(item);
      }
      if (item.item) {
        result.push(...findQuestionnaireResponseItems(item.item, linkId));
      }
    }

    return result;
  };

  const extractAnswers = (items: QuestionnaireResponseItem[]) => {
    const answers: Record<string, QuestionnaireResponseItemAnswer> = {};

    for (const { linkId, answer, item } of items) {
      if (item) {
        const subGroupAnswers: Record<string, QuestionnaireResponseItemAnswer> = {};
        item.forEach((subItem) => {
          if (subItem.answer) {
            subGroupAnswers[subItem.linkId] = subItem.answer?.[0] ?? {};
          }
        });
        answers[linkId] = subGroupAnswers;
      } else {
        answers[linkId] = answer?.[0] ?? {};
      }
    }

    return answers;
  };

  const questionnaireResponseItems = findQuestionnaireResponseItems(response.item, linkId);

  if (!questionnaireResponseItems.length) {
    return {};
  }

  const result: Record<string, Record<string, QuestionnaireResponseItemAnswer>[]> = {};

  // Maps answers by the groupByLinkId
  for (const questionnaireResponseItem of questionnaireResponseItems) {
    const answer = extractAnswers(questionnaireResponseItem.item || []);
    const identifier = get([answer[groupByLinkId]]);
    let key;
    if ((identifier as Coding)?.code) {
      key = (identifier as Coding).code?.toString() ?? "";
    } else {
      key = identifier?.toString() ?? "";
    }

    if (!result[key]) {
      result[key] = [];
    }

    result[key].push(answer);
  }

  return result;
}

export function getQuestionnaireFormsetResponses(
  questionnaireResponse: QuestionnaireResponse,
  formsetName: string,
  formsetCode: string,
): Record<string, QuestionnaireResponseItemAnswer>[] {
  return Object.values(getFormsetAnswers(questionnaireResponse, formsetName, formsetCode))
    .map((answer) => answer[0])
    .filter((answer) => !isEmpty(answer));
}

export function getAnswer(
  response: QuestionnaireResponse,
  linkId: string,
): QuestionnaireResponseItemAnswer | undefined {
  const findItem = (
    items: QuestionnaireResponseItem[] | undefined,
    targetLinkId: string,
  ): QuestionnaireResponseItem | undefined => {
    for (const item of items ?? []) {
      if (item.linkId === targetLinkId) return item;
      if (item.item) {
        const found = findItem(item.item, targetLinkId);
        if (found) return found;
      }
    }
    return undefined;
  };

  const item = findItem(response.item, linkId);
  return item?.answer?.[0];
}

/**
 * Returns a Set of all item linkIds present anywhere in the QuestionnaireResponse tree,
 * regardless of whether they carry answers. Use this to distinguish a deliberate empty
 * submission (item present, answer: []) from a partial update (item absent entirely).
 *
 * @param response - The QuestionnaireResponse to inspect.
 * @returns A Set of linkId strings for every item found in the response tree.
 */
export function getPresentLinkIds(response: QuestionnaireResponse): Set<string> {
  const result = new Set<string>();

  const collect = (items: QuestionnaireResponseItem[] | undefined): void => {
    for (const item of items ?? []) {
      result.add(item.linkId);
      collect(item.item);
    }
  };

  collect(response.item);
  return result;
}

export function itemIsAccepted(items: QuestionnaireResponseItem[]): boolean {
  return items.length > 0
    ? items.every(
        (item) =>
          getExtensionValue(item, Extensions.QuestionnaireResponseItemIsAccepted) as boolean,
      )
    : false;
}

/**
 * True when a coded answer means "nothing to report" rather than a real finding.
 * The intake UI keeps these as selectable options but never opens a formset row for them
 * (see EMPTY_ANSWER_LABELS in app/intake-forms/utils/multi-select.ts), so the bot must not
 * turn them into clinical resources either.
 *
 * @param code - The answer code to inspect.
 * @returns True if the code is an empty-answer option.
 */
export function isEmptyAnswerCode(code: string | undefined): boolean {
  if (!code) return false;
  return code === "none" || code.endsWith("/none");
}

export function convertYesNoToBoolean(value: string | { toString(): string }): boolean {
  const stringValue = typeof value === "string" ? value : value.toString();
  return stringValue === "Yes";
}

export class AnswerBuilder {
  constructor(private answers: Record<string, QuestionnaireResponseItemAnswer>) {}

  private getFirstValue(key: string): unknown {
    const item = this.answers[key];
    if (!item) return;

    const valueKey = ANSWER_TYPES.find((k) => !isEmpty(item[k]));
    return valueKey ? item[valueKey] : undefined;
  }

  valueString(key: string): string | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "string" ? val : undefined;
  }

  valueInteger(key: string): number | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "number" ? val : undefined;
  }

  valueDate(key: string): string | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val) ? val : undefined;
  }

  valueBoolean(key: string): boolean | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "boolean" ? val : undefined;
  }

  valueCoding(key: string): Coding | undefined {
    const val = this.getFirstValue(key);
    return typeof val === "object" && val !== null && "code" in val ? (val as Coding) : undefined;
  }

  valueYesNoToBoolean(key: string): boolean | undefined {
    const val = this.valueString(key);
    return val ? convertYesNoToBoolean(val) : undefined;
  }
}
